import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  inject,
  Input,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ErrorsHostDirective } from '@app/core/forms/directives/errors-host.directive';
import { IfInvalidDirective } from '@app/core/forms/directives/if-invalid.directive';
import { UserPaymentInfo } from '@app/entities/users/interfaces/user-payment-info';
import { ProfileSelectors } from '@app/store';
import { Store } from '@ngxs/store';
import { filter, Observable, tap } from 'rxjs';
import { IMask, IMaskModule } from 'angular-imask';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-payment-information',
  templateUrl: './payment-information.component.html',
  styleUrls: ['./payment-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IfInvalidDirective,
    ErrorsHostDirective,
    IMaskModule,
  ],
})
export class PaymentInformationComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private destroyRef: DestroyRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  @Input() required: boolean = false;

  form!: FormGroup;

  protected payment_info$: Observable<UserPaymentInfo | undefined> = inject(
    Store,
  ).select(ProfileSelectors.payment_info);

  protected isBrowser!: boolean;

  // Credit Card Number Maskfmask
  protected cardNumberMask = {
    mask: [
      {
        mask: '0000 000000 00000',
        regex: '^3[47]\\d{0,13}', // American Express
        cardtype: 'american express',
      },
      {
        mask: '0000 0000 0000 0000',
        regex: '^(?:6011|65\\d{0,2}|64[4-9]\\d?)\\d{0,12}', // Discover
        cardtype: 'discover',
      },
      {
        mask: '0000 000000 0000',
        regex: '^3(?:0([0-5]|9)|[689]\\d?)\\d{0,11}', // Diners
        cardtype: 'diners',
      },
      {
        mask: '0000 0000 0000 0000',
        regex: '^(5[1-5]\\d{0,2}|22[2-9]\\d{0,1}|2[3-7]\\d{0,2})\\d{0,12}', // Mastercard
        cardtype: 'mastercard',
      },
      {
        mask: '0000 0000 0000 0000',
        regex: '^4\\d{0,15}', // Visa
        cardtype: 'visa',
      },
      {
        mask: '0000 0000 0000 0000',
        regex: '^62\\d{0,14}', // UnionPay
        cardtype: 'unionpay',
      },
      {
        mask: '0000 0000 0000 0000',
        cardtype: 'Unknown',
      },
    ],
    dispatch: (appended: any, dynamicMasked: any) => {
      // Remove non-digit characters and concatenate with the appended value
      const number = (dynamicMasked.value + appended).replace(/\D/g, '');

      // Loop through all compiled masks and check if any match the input number
      for (let i = 0; i < dynamicMasked.compiledMasks.length; i++) {
        const re = new RegExp(dynamicMasked.compiledMasks[i].regex);
        if (number.match(re) != null) {
          return dynamicMasked.compiledMasks[i]; // Return the matching mask if found
        }
      }

      // If no mask matched, return the default mask (e.g., "Unknown" mask)
      return dynamicMasked.compiledMasks[
        dynamicMasked.compiledMasks.length - 1
      ];
    },
  };

  protected expirationDateMask = {
    mask: 'MM{/}YY',
    blocks: {
      MM: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
      },
      YY: {
        mask: IMask.MaskedRange,
        from: 0,
        to: 99,
      },
    },
  };

  protected cvcMask = {
    mask: '0000',
  };

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.form = this.buildForm();
    this.populateForm();
  }

  private buildForm(): FormGroup {
    const formGroup = this.formBuilder.group({
      card_number: [null],
      expiration_date: [null],
      cvc: [null],
    });

    if (this.required) {
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (control) {
          control.addValidators(Validators.required);
          control.updateValueAndValidity(); // Ensure the validators are applied immediately
        }
      });
    }

    return formGroup;
  }

  private populateForm(): void {
    this.payment_info$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value: UserPaymentInfo | undefined) => !!value),
        tap((userPaymentInfo: UserPaymentInfo) => {
          this.cardNumberFC.setValue(userPaymentInfo.card_number);
          this.expirationDateFC.setValue(userPaymentInfo.expiration_date);
          this.cvcFC.setValue(userPaymentInfo.cvc);
        }),
      )
      .subscribe();
  }

  protected get cardNumberFC(): FormControl {
    return this.form.get('card_number') as FormControl;
  }

  protected get expirationDateFC(): FormControl {
    return this.form.get('expiration_date') as FormControl;
  }

  protected get cvcFC(): FormControl {
    return this.form.get('cvc') as FormControl;
  }
}
